import { Injectable, signal } from '@angular/core';
import type { IStepOption } from 'ngx-ui-tour-core';
import type { KirigamiTourStepView } from './tour.types';

/**
 * Root-level bridge between the (per-element) tour anchor directives and the
 * single popover/backdrop host. The directive that owns the active step calls
 * `show()`; the host component renders from the `active` signal.
 */
@Injectable({ providedIn: 'root' })
export class KirigamiTourStepHostService {
  private readonly _active = signal<KirigamiTourStepView | null>(null);
  /** The step currently being displayed, or null when no tour is running. */
  readonly active = this._active.asReadonly();

  private steps: IStepOption[] = [];

  /** Called by the orchestration service when a tour starts, for progress info. */
  setSteps(steps: IStepOption[]): void {
    this.steps = steps;
  }

  /** Show a step anchored to `anchorEl`. Index/total derived from `setSteps`. */
  show(anchorEl: HTMLElement, step: IStepOption): void {
    const index = this.steps.findIndex((s) => s.stepId === step.stepId);
    this._active.set({
      anchorEl,
      step,
      index: index < 0 ? 0 : index,
      total: this.steps.length || 1,
    });
  }

  /** Hide the popover (tour ended or step hidden). */
  hide(): void {
    this._active.set(null);
  }
}
